import { Calendar, ChevronDown, ChevronUp, Clock, MapPin, Settings, Target, Users, Wrench } from 'lucide-react';
import { LAYOUT_CONFIG, SERVICE_CONFIG } from '../../constants/booking-details.constants';
import { cn } from '../../lib/utils';
import { BookingDetails, ExpandedSections } from '../../types/booking-details.types';
import { formatBookingAmount, formatBookingDate } from '../../utils/booking-details.utils';
import { ClientSection } from './ClientSection';
import { DetailItem } from './DetailItem';
import { ExpandableSection } from './ExpandableSection';
import { FinancesSection } from './FinancesSection';

export function DetailsTab({
  booking,
  expandedSections,
  toggleSection,
}: {
  booking: BookingDetails;
  expandedSections: ExpandedSections;
  toggleSection: (section: keyof ExpandedSections) => void;
}) {
  const b = booking;

  return (
    <div className="p-6">
      <div className="space-y-6">
        {/* CLIENT & PARTICIPANTS */}
        <ExpandableSection title="Client & Participants" icon={Users} isExpanded={true} onToggle={() => {}}>
          <ClientSection booking={b} expandedSections={expandedSections} toggleSection={toggleSection} />
        </ExpandableSection>

        {/* ESPACE & CONFIGURATION */}
        <ExpandableSection title="Espace & Configuration" icon={MapPin} isExpanded={true} onToggle={() => {}}>
          <div className="grid grid-cols-2 gap-x-8 gap-y-6 mb-6">
            <DetailItem label="Espace" value={b.space.name} sub={`${b.space.type} · Cap. ${b.space.capacity} max`} />
            <DetailItem
              label="Participants"
              value={`${b.details.attendees} personne(s)`}
              icon={<Users className="w-4 h-4 text-slate-400" />}
            />
            <DetailItem
              label="Dates"
              value={formatBookingDate(b.details.startDate)}
              sub={b.details.startDate !== b.details.endDate ? `au ${formatBookingDate(b.details.endDate)}` : undefined}
              icon={<Calendar className="w-4 h-4 text-slate-400" />}
            />
            <DetailItem
              label="Horaires"
              value={`${b.details.startTime} – ${b.details.endTime}`}
              sub={b.details.duration}
              icon={<Clock className="w-4 h-4 text-slate-400" />}
            />
          </div>

          {/* Configuration Physique */}
          {b.space.configuration && (
            <div className="mb-6">
              <button
                onClick={() => toggleSection('configuration')}
                className="flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-primary transition-colors mb-3"
              >
                <Settings className="w-4 h-4" />
                Configuration Physique
                {expandedSections.configuration ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </button>
              {expandedSections.configuration && (
                <div className="p-4 bg-slate-50 rounded-lg space-y-4">
                  {/* Image de la mise en place */}
                  {b.space.configuration.layoutImageUrl && (
                    <div className="mb-4">
                      <img
                        src={b.space.configuration.layoutImageUrl}
                        alt={b.space.configuration.layout}
                        className="w-full h-48 object-cover rounded-lg border border-slate-200"
                      />
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <DetailItem
                      label="Disposition"
                      value={LAYOUT_CONFIG[b.space.configuration.layout] || b.space.configuration.layout}
                    />
                    <DetailItem
                      label="Capacité configurée"
                      value={`${b.space.configuration.configuredCapacity} places`}
                    />
                  </div>

                  {/* Description de la mise en place */}
                  {b.space.configuration.layoutDescription && (
                    <div className="pt-3 border-t border-slate-200">
                      <span className="text-[11px] font-bold uppercase tracking-widest text-slate-400">
                        Description
                      </span>
                      <p className="text-sm text-slate-700 mt-2">{b.space.configuration.layoutDescription}</p>
                    </div>
                  )}

                  {b.space.configuration.includedEquipment.length > 0 && (
                    <div className="pt-3 border-t border-slate-200">
                      <span className="text-[11px] font-bold uppercase tracking-widest text-slate-400">
                        Équipements inclus
                      </span>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {b.space.configuration.includedEquipment.map((equipment, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-1 bg-secondary/10 text-secondary text-xs font-medium rounded-md"
                          >
                            {equipment}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Services Additionnels */}
          {b.services && b.services.length > 0 && (
            <div>
              <button
                onClick={() => toggleSection('services')}
                className="flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-primary transition-colors mb-3"
              >
                <Wrench className="w-4 h-4" />
                Services Additionnels ({b.services.length})
                {expandedSections.services ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              {expandedSections.services && (
                <div className="space-y-3">
                  {b.services.map((service, idx) => {
                    const config = SERVICE_CONFIG[service.category] || SERVICE_CONFIG.equipment;
                    const ServiceIcon = config.icon;
                    return (
                      <div key={idx} className="p-4 bg-slate-50 rounded-lg">
                        <div className="flex items-start gap-3">
                          <div className={cn('p-2 rounded-lg', config.bg, config.text)}>
                            <ServiceIcon className="w-4 h-4" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-1">
                              <p className="font-semibold text-slate-900">{service.name}</p>
                              <p className="font-bold text-slate-900">{formatBookingAmount(service.totalPrice)}</p>
                            </div>
                            <div className="text-sm text-slate-600 space-y-1">
                              <p>
                                {formatBookingAmount(service.unitPrice)} × {service.quantity} ={' '}
                                {formatBookingAmount(service.totalPrice)}
                              </p>
                              {service.details && <p className="italic">{service.details}</p>}
                              {service.schedule && (
                                <p className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {service.schedule}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Motif/Note */}
          {b.details.purpose && (
            <div className="pt-5 mt-5 border-t border-slate-200">
              <span className="text-[11px] font-bold uppercase tracking-widest text-slate-400">Motif / Note</span>
              <p className="text-sm text-slate-800 mt-2 italic">"{b.details.purpose}"</p>
            </div>
          )}

          {/* Réponses au formulaire dynamique */}
          {b.details.specificDetails && Object.keys(b.details.specificDetails).length > 0 && (
            <div className="pt-5 mt-5 border-t border-slate-200">
              <span className="text-[11px] font-bold uppercase tracking-widest text-slate-400">
                Détails supplémentaires
              </span>

              {/* Affichage spécial pour les équipements */}
              {b.details.specificDetails['Équipements sélectionnés'] && (
                <div className="mt-4 mb-6 p-4 bg-slate-50 rounded-lg border border-slate-100">
                  <h4 className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-3 flex items-center gap-2">
                    <Wrench className="w-3.5 h-3.5" />
                    Équipements sélectionnés
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {String(b.details.specificDetails['Équipements sélectionnés'])
                      .split(',')
                      .map((eq, i) => {
                        const str = eq.trim();
                        if (!str) return null;
                        return (
                          <span
                            key={i}
                            className="px-2.5 py-1 bg-white border border-slate-200 text-slate-700 text-xs font-medium rounded-md shadow-sm"
                          >
                            {str}
                          </span>
                        );
                      })}
                  </div>
                </div>
              )}

              <div className="mt-3 space-y-3">
                {(() => {
                  const entries = Object.entries(b.details.specificDetails);

                  // Définir l'ordre prioritaire
                  const priorityOrder = ['Date de début', 'Date de fin', 'Nombre de jours'];

                  // Séparer les entrées prioritaires et les autres
                  const priorityEntries = priorityOrder
                    .map((key) => entries.find(([k]) => k === key))
                    .filter(Boolean) as [string, any][];

                  const otherEntries = entries.filter(
                    ([key]) => !priorityOrder.includes(key) && key !== 'Équipements sélectionnés',
                  );

                  // Combiner dans l'ordre
                  const orderedEntries = [...priorityEntries, ...otherEntries];

                  return orderedEntries.map(([key, value]) => {
                    // Les labels et valeurs sont déjà traduits dans transformBookingDetails
                    const displayValue = typeof value === 'string' ? value : String(value ?? '—');

                    // Affichage spécial pour les objectifs (pleine largeur avec fond)
                    if (key.toLowerCase().includes('objectif')) {
                      return (
                        <div
                          key={key}
                          className="p-4 bg-gradient-to-br from-slate-50 to-slate-100/50 rounded-lg border border-slate-200"
                        >
                          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-2">
                            <Target className="w-3.5 h-3.5" />
                            {key}
                          </p>
                          <p className="text-sm text-slate-700 leading-relaxed">{displayValue || '—'}</p>
                        </div>
                      );
                    }

                    // Affichage normal : label et valeur sur la même ligne
                    return (
                      <div
                        key={key}
                        className="flex items-start justify-between gap-4 py-2 border-b border-slate-100 last:border-0"
                      >
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide min-w-[140px]">
                          {key}
                        </p>
                        <p className="text-sm text-slate-900 font-medium text-right flex-1">{displayValue || '—'}</p>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          )}
        </ExpandableSection>

        {/* FINANCES & FACTURATION */}
        <FinancesSection booking={b} expandedSections={expandedSections} toggleSection={toggleSection} />
      </div>
    </div>
  );
}
